// File Name: src/pages/SignUpPage.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Form, Input, Button, Typography, message } from 'antd';
import { MailOutlined, LockOutlined, UserOutlined, GoogleOutlined, PhoneOutlined } from '@ant-design/icons';
import { signUpWithEmail, signInWithGoogle } from '../lib/firebase';
import { userServices } from '../lib/databaseService';

const { Title, Text } = Typography;

const SignUpPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const cardStyle = {
    maxWidth: 400,
    margin: '60px auto',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const { user: authUser } = await signUpWithEmail(values.email, values.password);
      
      // Create user data object with additional profile information
      const userData = {
        uid: authUser.uid,
        email: authUser.email,
        displayName: values.displayName,
        phoneNumber: values.phoneNumber || '',
        photoURL: '',
        profileCompleted: false
      };
      
      // Add user data to Firestore using the new service
      await userServices.addUserToFirestore(userData);
      
      message.success('Sign up successful! Welcome to Yalla Nellab.');
      navigate('/'); // Navigate to home after sign up
    } catch (error) {
      console.error('Sign Up Error:', error);
      if (error.code === 'auth/email-already-in-use') {
        message.error('This email is already registered. Please sign in instead.');
      } else if (error.code === 'auth/weak-password') {
        message.error('Password is too weak. Please use a stronger password.');
      } else if (error.code === 'auth/invalid-email') {
        message.error('Invalid email address. Please check your email.');
      } else {
        message.error(error.message || 'Failed to sign up. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { user: authUser } = await signInWithGoogle();
      
      // Check if user already exists in Firestore
      const existingUser = await userServices.getUserFromFirestore(authUser.uid);
      
      if (!existingUser) {
        // Create user data object for new Google sign-up
        const userData = {
          uid: authUser.uid,
          email: authUser.email,
          displayName: authUser.displayName || authUser.email.split('@')[0],
          phoneNumber: '',
          photoURL: authUser.photoURL || '',
          profileCompleted: false
        };
        
        // Add new user to Firestore
        await userServices.addUserToFirestore(userData);
        message.success('Google sign up successful! Welcome to Yalla Nellab.');
      } else {
        message.success('Signed in with Google successfully! Welcome back.');
      }
      
      navigate('/');
    } catch (error) {
      console.error('Google Sign In Error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        message.error('Sign in was cancelled.');
      } else if (error.code === 'auth/popup-blocked') {
        message.error('Popup was blocked. Please allow popups for this site.');
      } else {
        message.error(error.message || 'Failed to sign in with Google.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Row justify="center" style={{ minHeight: '80vh', backgroundColor: '#F0F2F5', padding: '20px' }}>
      <Col>
        <Card style={cardStyle}>
          <Title level={3} style={{ textAlign: 'center', color: '#6e4598', marginBottom: '10px' }}>
            Create Your Account
          </Title>
          
          <Text type="secondary" style={{ textAlign: 'center', display: 'block', marginBottom: '20px' }}>
            Join Yalla Nellab Organization today
          </Text>
          
          <Button 
            icon={<GoogleOutlined />}
            size="large"
            block
            style={{ 
              marginBottom: 20, 
              backgroundColor: '#4285F4', 
              color: 'white',
              borderColor: '#4285F4',
              height: '45px'
            }}
            onClick={handleGoogleSignIn}
            loading={googleLoading}
          >
            Sign Up with Google
          </Button>

          <div style={{ textAlign: 'center', margin: '20px 0', position: 'relative' }}>
            <div style={{ 
              position: 'absolute', 
              top: '50%', 
              left: 0, 
              right: 0, 
              height: '1px', 
              backgroundColor: '#d9d9d9' 
            }} />
            <Text 
              type="secondary" 
              style={{ 
                backgroundColor: 'white', 
                padding: '0 15px',
                fontSize: '12px'
              }}
            >
              OR SIGN UP WITH EMAIL
            </Text>
          </div>
          
          <Form
            name="signup"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            layout="vertical"
            size="large"
          >
            <Form.Item
              label="Full Name"
              name="displayName"
              rules={[{ 
                required: true, 
                message: 'Please input your full name!' 
              }]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="Enter your full name" 
              />
            </Form.Item>
            
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { 
                  required: true, 
                  message: 'Please input your email!' 
                }, 
                { 
                  type: 'email', 
                  message: 'Please enter a valid email address!' 
                }
              ]}
            >
              <Input 
                prefix={<MailOutlined />} 
                placeholder="Enter your email" 
              />
            </Form.Item>

            <Form.Item
              label="Phone Number (Optional)"
              name="phoneNumber"
              rules={[
                {
                  pattern: /^\+?[\d\s\-\(\)]+$/,
                  message: 'Please enter a valid phone number!',
                }
              ]}
            >
              <Input 
                prefix={<PhoneOutlined />} 
                placeholder="+1 234 567 8900" 
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                { 
                  required: true, 
                  message: 'Please input your password!' 
                }, 
                { 
                  min: 6, 
                  message: 'Password must be at least 6 characters.' 
                }
              ]}
              hasFeedback
            >
              <Input.Password 
                prefix={<LockOutlined />} 
                placeholder="Create a password" 
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['password']}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Please confirm your password!',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password 
                prefix={<LockOutlined />} 
                placeholder="Confirm your password" 
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: '10px' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                block 
                loading={loading} 
                style={{ 
                  backgroundColor: '#6e4598', 
                  borderColor: '#6e4598',
                  height: '45px',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                Create Account
              </Button>
            </Form.Item>
          </Form>
          
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Text>
              Already have an account?{' '}
              <Button 
                type="link" 
                onClick={() => navigate('/signin')} 
                style={{ 
                  padding: 0, 
                  fontWeight: '600',
                  color: '#6e4598'
                }}
              >
                Sign In
              </Button>
            </Text>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default SignUpPage;